interface InputFieldColors {
  background?: string | null;
  border?: string | null;
  text?: string | null;
  error?: string | null;
}
interface InputFieldStyle {
  font?: string | null;
  cornerRadius?: number | null;
  borderWidth?: number | null;
  padding?: number | null;
}
export interface InputFields {
  colors?: InputFieldColors | null;
  style?: InputFieldStyle | null;
}

const INPUT_WRAPPER_SELECTOR = "label.bg-color-primary:has(input)";
const TEXTAREA_WRAPPER_SELECTOR = "div:has(> label > textarea)";

export function buildInputFieldRules(
  s: string,
  inputFields: InputFields | null | undefined,
): string[] {
  if (!inputFields) return [];
  const colors = inputFields.colors ?? undefined;
  const style = inputFields.style ?? undefined;

  const background = colors?.background || undefined;
  const border = colors?.border || undefined;
  const text = colors?.text || undefined;

  const wrapperProps: string[] = [];
  if (background) {
    // `!important` beats the inline background on the readonly-branch label.
    wrapperProps.push(`background-color: ${background} !important;`);
  }
  if (border) {
    wrapperProps.push(`border-color: ${border} !important;`);
  }
  if (typeof style?.cornerRadius === "number") {
    wrapperProps.push(`border-radius: ${style.cornerRadius}px !important;`);
  }
  if (typeof style?.borderWidth === "number") {
    wrapperProps.push(`border-width: ${style.borderWidth}px !important;`);
  }
  if (typeof style?.padding === "number") {
    wrapperProps.push(`padding: ${style.padding}px !important;`);
  }
  if (style?.font) {
    wrapperProps.push(`font-family: ${style.font};`);
  }

  const inputWrapperRule =
    wrapperProps.length > 0
      ? `${s}${INPUT_WRAPPER_SELECTOR} { ${wrapperProps.join(" ")} }`
      : undefined;
  const textareaWrapperRule =
    wrapperProps.length > 0
      ? `${s}${TEXTAREA_WRAPPER_SELECTOR} { ${wrapperProps.join(" ")} }`
      : undefined;

  // Override the design-system's focus-within border swap with a derived ring
  // so the selected state stays visually distinct even when border === border.
  const focusShadow = border
    ? `0 0 0 3px color-mix(in srgb, ${border} 30%, transparent)`
    : undefined;
  const inputFocusRule = border
    ? `${s}${INPUT_WRAPPER_SELECTOR}:focus-within { border-color: ${border} !important; box-shadow: ${focusShadow} !important; }`
    : undefined;
  const textareaFocusRule = border
    ? `${s}${TEXTAREA_WRAPPER_SELECTOR}:focus-within { border-color: ${border} !important; box-shadow: ${focusShadow} !important; }`
    : undefined;

  // Target the `<input>`/`<textarea>` directly so the merchant's color beats
  // the primary text token inherited from the wrapping label. Placeholder
  // color is derived from the text color at reduced opacity.
  const placeholderColor = text
    ? `color-mix(in srgb, ${text} 50%, transparent)`
    : undefined;
  const inputTextRule = text
    ? `${s}${INPUT_WRAPPER_SELECTOR} input { color: ${text} !important; }`
    : undefined;
  const inputPlaceholderRule = text
    ? `${s}${INPUT_WRAPPER_SELECTOR} input::placeholder { color: ${placeholderColor} !important; }`
    : undefined;
  const textareaTextRule = text
    ? `${s}${TEXTAREA_WRAPPER_SELECTOR} textarea { color: ${text} !important; }`
    : undefined;
  const textareaPlaceholderRule = text
    ? `${s}${TEXTAREA_WRAPPER_SELECTOR} textarea::placeholder { color: ${placeholderColor} !important; }`
    : undefined;

  return [
    inputWrapperRule,
    textareaWrapperRule,
    inputFocusRule,
    textareaFocusRule,
    inputTextRule,
    inputPlaceholderRule,
    textareaTextRule,
    textareaPlaceholderRule,
  ].filter((rule): rule is string => rule !== undefined);
}
